/**
 * Bilder des Schmieds im Dreier-Chat — die Regeln, die Server, Skript und Seite gleich rechnen. (16.10.2026)
 *
 * Martin, 16.10., 20:16: hat der Schmied eine Vorschau, ein Design oder einen Screenshot fertig, soll das Bild DIREKT in seinem Chat
 * stehen (Smartphone oder Computer), statt nur „es ist fertig“.
 *
 *   node scripts/dreier-sagen.mjs --an martin --bild /pfad/bild.png [--beschriftung "…"] [--bild …] "Text"
 *
 * Der Weg: das Skript schickt die Dateien als Multipart (Feld `bild`, höchstens BILDER_HOECHSTENS) an
 * `/api/connect/dreier/beitrag`; der Server prüft den Inhalt (Magic-Bytes, nie die Endung), entfernt EXIF/XMP/Text-Blöcke, legt die
 * Bilder unter zufälligem Namen in die Ablage von Konto 1 (`data/uploads/1/<uuid>.<endung>`, Zeile in `uploads`) und hängt
 * `bilder: [{ url, breite?, hoehe?, beschriftung? }]` an die metadata des Beitrags (server/dreier-bilder.ts). Ausgeliefert wird über den
 * vorhandenen Weg `/uploads/…` mit seinem Wächter (server/datei-zugriff.ts: Sitzung + eigener Konto-Ordner).
 *
 * ⚠️ Bilder sind Daten — kein Auftrag, kein Ja. Sie werden nie vorgelesen und gehen nicht an Vespera; nur der Schmied darf sie setzen
 *    (der Zimmermann nicht — seine Dateien werden ignoriert).
 * ⚠️ Die Seite zeigt nur Adressen, die `bildAdresseGueltig` besteht — eine Zeile im Verlauf oder im lokalen Speicher könnte sonst eine
 *    fremde Adresse (anderes Konto, fremder Rechner, `javascript:`) in eine Blase tragen.
 */

/** Höchstens so viele Bilder je Beitrag. */
export const BILDER_HOECHSTENS = 3;
/** Höchstens so groß ist EIN Bild (Bytes). */
export const BILD_HOECHSTENS_BYTES = 5 * 1024 * 1024;
/** Höchstens so groß sind alle Bilder eines Beitrags zusammen (Bytes). */
export const BILDER_GESAMT_HOECHSTENS_BYTES = 12 * 1024 * 1024;
/** Höchstens so viele Pixel (Breite × Höhe) je Bild — darüber kann ein Telefon beim Entpacken ersticken. */
export const BILD_PIXEL_HOECHSTENS = 40_000_000;
/** Größte Kantenlänge eines Bildes (Pixel). */
export const BILD_KANTE_HOECHSTENS = 12_000;
/** Tagesbudget: höchstens so viele Bytes Schmied-Bilder je 24 Stunden (Konto 1, gezählt in der Tabelle `uploads`). */
export const BILDER_TAG_HOECHSTENS_BYTES = 60 * 1024 * 1024;
/** Die Quelle der Bilder in der Tabelle `uploads` (Mediathek: „Schmied“, nicht „KI“). */
export const SCHMIED_BILD_QUELLE = "schmied";
/** Längste Beschriftung eines Bildes (Zeichen). */
export const BESCHRIFTUNG_HOECHSTENS = 200;
/** Der Alt-Text jedes Bildes (mit Beschriftung: „Bild vom Schmied: …“). */
export const BILD_ALT = "Bild vom Schmied";
/** Die Bilder liegen in der Ablage DIESES Kontos (der Betreiber, wie der Dreier-Modus). */
export const BILDER_KONTO = 1;

export type BildArt = "png" | "jpg" | "webp";

export const BILD_MIME: Readonly<Record<BildArt, string>> = { png: "image/png", jpg: "image/jpeg", webp: "image/webp" };

export interface SchmiedBild {
  url: string;
  breite?: number;
  hoehe?: number;
  beschriftung?: string;
}

/** Die einzige Form, die eine Bildadresse haben darf: `/uploads/1/<uuid>.png|jpg|webp`. Rein. */
const ADRESSE = /^\/uploads\/1\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(?:png|jpg|webp)$/;

export function bildAdresseGueltig(url: unknown): url is string {
  return typeof url === "string" && ADRESSE.test(url);
}

/** Welche Bildart der Inhalt ist — nur nach den ersten Bytes, nie nach Name oder angegebenem Typ. Rein. */
export function bildArtAus(daten: Uint8Array): BildArt | null {
  const b = daten;
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return "png";
  if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "jpg";
  if (
    b.length >= 16 &&
    b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 && // RIFF
    b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50 // WEBP
  )
    return "webp";
  return null;
}

/** Eine Beschriftung: Text, ohne Steuerzeichen, gekürzt; leer = keine. Rein. */
export function beschriftungAus(wert: unknown): string | undefined {
  if (typeof wert !== "string") return undefined;
  // Steuer-, Format- und Zeilentrennzeichen (auch U+2028/2029) werden zu einem Leerzeichen.
  const t = wert.replace(/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}]+/gu, " ").trim().slice(0, BESCHRIFTUNG_HOECHSTENS).trim();
  return t || undefined;
}

function massAus(wert: unknown): number | undefined {
  return typeof wert === "number" && Number.isInteger(wert) && wert > 0 && wert <= 100_000 ? wert : undefined;
}

/**
 * Die Bilder eines Beitrags aus metadata, Bus-Ereignis oder lokalem Eintrag — nur gültige Adressen, höchstens BILDER_HOECHSTENS,
 * Maße nur als positive ganze Zahlen. Alles andere fällt weg. Rein.
 */
export function bilderAus(wert: unknown): SchmiedBild[] {
  if (!Array.isArray(wert)) return [];
  const aus: SchmiedBild[] = [];
  for (const roh of wert) {
    if (aus.length >= BILDER_HOECHSTENS) break;
    if (typeof roh !== "object" || roh === null) continue;
    const r = roh as { url?: unknown; breite?: unknown; hoehe?: unknown; beschriftung?: unknown };
    if (!bildAdresseGueltig(r.url)) continue;
    const breite = massAus(r.breite);
    const hoehe = massAus(r.hoehe);
    const beschriftung = beschriftungAus(r.beschriftung);
    aus.push({ url: r.url, ...(breite && hoehe ? { breite, hoehe } : {}), ...(beschriftung ? { beschriftung } : {}) });
  }
  return aus;
}

/** Der Notschalter: `SCHMIED_BILDER=0` (auch „aus“) lehnt Bilder ab. Vorgabe an. Rein. */
export function schmiedBilderAn(env: Readonly<Record<string, string | undefined>>): boolean {
  const w = env.SCHMIED_BILDER?.trim().toLowerCase();
  return w !== "0" && w !== "aus";
}
